import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Shield, Heart, Award } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const ABOUT_IMG = "https://images.pexels.com/photos/7108324/pexels-photo-7108324.jpeg?auto=compress&cs=tinysrgb&w=900";
const TEAM_IMG = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=700";

const WHY_TRUST_ICONS = [<Shield />, <Shield />, <Shield />, <Heart />, <Award />, <Heart />];

export default function About() {
  const { t } = useTranslation();
  const [missionRef, missionVisible] = useScrollAnimation();
  const [valuesRef, valuesVisible] = useScrollAnimation();
  const [mtRef, mtVisible] = useScrollAnimation();

  const trustItems = t("whyChoose.items", { returnObjects: true }) || [];
  const values = t("about.values.items", { returnObjects: true }) || [];

  return (
    <div className="pt-20">
      <Helmet>
        <title>About Us | Trusted Medical Tourism Facilitator in Turkey | Monimek</title>
        <meta name="description" content="Learn why thousands of US and Canadian patients trust Monimek for safe, affordable, and high-quality medical procedures in Turkey." />
        <link rel="canonical" href="https://www.monizmek.com/about" />
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <img src={ABOUT_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 py-16 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("about.hero.overline")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight mt-3 mb-5" data-testid="about-hero-title">
            {t("about.hero.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-xl mx-auto">{t("about.hero.subtitle")}</p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#F8FAFC]" ref={missionRef}>
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className={`anim-init ${missionVisible ? "in-view" : ""}`}>
            <span className="overline-text">{t("about.mission.overline")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-5">
              {t("about.mission.title")}
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-8">{t("about.mission.text")}</p>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <span className="overline-text">{t("about.vision.overline")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-5">
                {t("about.vision.title")}
              </h2>
              <p className="text-[#6B7280] leading-relaxed">{t("about.vision.text")}</p>
            </div>
          </div>

          <div className={`anim-init stagger-2 ${missionVisible ? "in-view" : ""} relative`}>
            <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(11,60,93,0.12)]" style={{ aspectRatio: "4/3" }}>
              <img src={ABOUT_IMG} alt="Modern clinic" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -right-5 glass-card rounded-2xl p-5 shadow-xl">
              <div className="text-3xl font-black text-[#0B3C5D] font-outfit">500+</div>
              <div className="text-sm text-[#6B7280]">Happy Patients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white" ref={valuesRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`text-center mb-12 anim-init ${valuesVisible ? "in-view" : ""}`}>
            <span className="overline-text">{t("about.values.overline")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2">
              {t("about.values.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {values.map((val, i) => (
              <div
                key={i}
                className={`anim-init stagger-${i + 1} ${valuesVisible ? "in-view" : ""} bg-[#F8FAFC] rounded-3xl p-8 border border-gray-100 treatment-card text-center`}
                data-testid={`value-card-${i}`}
              >
                <div className="w-14 h-14 rounded-full why-icon flex items-center justify-center text-white mx-auto mb-5">
                  {i === 0 ? <Award className="w-7 h-7" /> : i === 1 ? <Heart className="w-7 h-7" /> : <Shield className="w-7 h-7" />}
                </div>
                <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-3">{val.title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Is Medical Tourism */}
      <section className="py-20 bg-[#F8FAFC]" ref={mtRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`grid lg:grid-cols-2 gap-14 items-center anim-init ${mtVisible ? "in-view" : ""}`}>
            <div>
              <span className="overline-text">{t("about.whatIsMT.overline")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2 mb-6">
                {t("about.whatIsMT.title")}
              </h2>
              <p className="text-[#6B7280] leading-relaxed mb-8">{t("about.whatIsMT.text")}</p>
              <Link to="/how-it-works" className="btn-primary inline-flex" data-testid="about-learn-more">
                How It Works <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(11,60,93,0.10)]" style={{ aspectRatio: "4/3" }}>
              <img src={TEAM_IMG} alt="Doctor consultation" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Families Trust Us */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight">{t("about.whyTrust")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustItems.map((item, i) => (
              <div
                key={i}
                className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex gap-4 items-start treatment-card"
                data-testid={`trust-item-${i}`}
              >
                <div className="w-9 h-9 rounded-xl why-icon flex items-center justify-center text-white flex-shrink-0">
                  <Shield className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0B3C5D] text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-[#6B7280] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-gradient py-16">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white font-outfit mb-4">{t("ctaBanner.title")}</h2>
          <p className="text-white/75 mb-8">{t("ctaBanner.subtitle")}</p>
          <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0B3C5D] font-bold rounded-full hover:scale-105 transition-all duration-200">
            {t("ctaBanner.button")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
